import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2, Sparkles, Lightbulb } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ENTITY_COLORS, ENTITY_TEXT, ENTITY_LABELS } from "@/lib/entityColors";
import { NodeFields, RelationshipList, LearnerActions } from "@/components/explore/DetailPanelParts";
import { ResearchPanel } from "@/components/ai/ResearchPanel";
import { SuggestEntityDialog } from "@/components/journey/SuggestEntityDialog";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export function DetailPanel({ selectedId, panelOpen, schema, learnedIds, onToggleLearned, onClose, onJumpTo }) {
  const { user } = useAuth();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [researchOpen, setResearchOpen] = useState(false);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [insertText, setInsertText] = useState(null);
  const [insertFieldKey, setInsertFieldKey] = useState("");

  useEffect(() => {
    if (!selectedId || !panelOpen) return;
    let alive = true;
    setLoading(true);
    api
      .get(`/kg/nodes/${selectedId}`)
      .then((res) => alive && setDetail(res.data))
      .catch(() => alive && toast.error("Could not load this entity."))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [selectedId, panelOpen, refresh]);

  const node = detail?.node;
  const data = node?.data || {};
  const type = node?.type;
  const fields = schema?.node_types?.[type]?.fields || [];
  const nameKey = type === "text" ? "title" : "name";
  const bodyFields = fields.filter(
    (f) => f.key !== nameKey && (Array.isArray(data[f.key]) ? data[f.key].length : data[f.key])
  );
  const edges = detail?.edges || [];
  const outgoing = node ? edges.filter((e) => e.source_id === node.id) : [];
  const incoming = node ? edges.filter((e) => e.target_id === node.id) : [];
  const learned = node ? learnedIds.includes(node.id) : false;
  const isCurator = user && (user.role === "curator" || user.role === "admin");
  const insertableFields = fields.filter((f) => f.key !== nameKey);
  const defaultInsertField = (
    fields.find((f) => ["description", "summary", "content", "overview", "definition"].includes(f.key)) ||
    insertableFields[0] || { key: "description" }
  ).key;

  const saveResearchToNotes = async (text, sources) => {
    try {
      await api.post("/notes", { entry_type: "note", node_id: node.id, text });
      for (const s of (sources || []).slice(0, 6)) {
        if (s.url) await api.post("/notes", { entry_type: "link", node_id: node.id, url: s.url, label: s.title });
      }
      toast.success("Saved to your journal");
      setResearchOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save. Please sign in."); }
  };

  const openInsertPicker = (text) => {
    setInsertFieldKey(defaultInsertField);
    setInsertText(text);
  };

  const confirmInsert = async () => {
    try {
      const res = await api.post(`/kg/nodes/${node.id}/append-field`, { field: insertFieldKey, text: insertText });
      toast.success(res.data.staged ? "Added as a pending edit — approve it in the Review Queue" : "Inserted into this entity");
      setInsertText(null);
      setResearchOpen(false);
      if (!res.data.staged) setRefresh((x) => x + 1);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not insert"); }
  };

  return (
    <AnimatePresence>
      {panelOpen && (
        <motion.aside
          key="detail-panel"
          initial={{ x: "100%" }}
          animate={{ x: 0 }}
          exit={{ x: "100%" }}
          transition={{ type: "tween", duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
          className="absolute top-0 right-0 h-full w-full sm:w-[440px] bg-[#fdfdfb] border-l border-[#14140f]/10 shadow-2xl z-20 overflow-y-auto"
          data-testid="detail-panel"
        >
          <div className="sticky top-0 flex justify-end p-3 bg-[#fdfdfb]/90 backdrop-blur-sm">
            <button onClick={onClose} data-testid="detail-panel-close" className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#f5f4f0] text-[#14140f]/60">
              <X className="w-4 h-4" />
            </button>
          </div>

          {loading || !node ? (
            <div className="flex items-center justify-center py-40">
              <Loader2 className="w-6 h-6 text-[#c23c28] animate-spin" />
            </div>
          ) : (
            <div className="px-7 pb-14">
              <span
                className="inline-block text-[11px] font-semibold uppercase tracking-wide px-2.5 py-1 rounded-full mb-4"
                style={{ background: ENTITY_COLORS[type], color: ENTITY_TEXT[type] }}
                data-testid="detail-type-badge"
              >
                {ENTITY_LABELS[type] || type}
              </span>
              <h2 className="font-serif text-3xl md:text-4xl tracking-tight text-[#14140f] mb-6" data-testid="detail-title">
                {node.label}
              </h2>

              {user && (
                <div className="flex flex-wrap items-center gap-2 mb-6">
                  <Button
                    size="sm"
                    onClick={() => setResearchOpen(true)}
                    data-testid="detail-research-btn"
                    className="bg-[#ffb020] hover:bg-[#e69a10] text-[#14140f]"
                  >
                    <Sparkles className="w-4 h-4 mr-1.5" /> Research with AI
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setSuggestOpen(true)}
                    data-testid="detail-suggest-entity-btn"
                    className="border-[#ff7360]/40 text-[#c23c28] hover:bg-[#ff7360]/10"
                  >
                    <Lightbulb className="w-4 h-4 mr-1.5" /> Suggest a new entity
                  </Button>
                </div>
              )}

              <NodeFields bodyFields={bodyFields} data={data} />
              <RelationshipList outgoing={outgoing} incoming={incoming} onJumpTo={onJumpTo} />
              <LearnerActions node={node} learned={learned} onToggleLearned={onToggleLearned} />

              <ResearchPanel
                open={researchOpen}
                onOpenChange={setResearchOpen}
                contextLabel={node.label}
                onSaveToNotes={saveResearchToNotes}
                onInsertEntity={isCurator ? openInsertPicker : undefined}
                enableExtract={isCurator}
                presetPrompt={`Tell me about ${node.label} in ancient Indian philosophy, with sources.`}
              />

              <SuggestEntityDialog
                open={suggestOpen}
                onOpenChange={setSuggestOpen}
              />

              <Dialog open={!!insertText} onOpenChange={(o) => !o && setInsertText(null)}>
                <DialogContent className="max-w-md" data-testid="insert-field-dialog">
                  <DialogHeader>
                    <DialogTitle className="font-serif text-xl">Insert into which field?</DialogTitle>
                    <DialogDescription>
                      The research will be appended to the chosen field of <b className="text-foreground">{node.label}</b>.
                      Edits to published entities are staged for review.
                    </DialogDescription>
                  </DialogHeader>
                  <Select value={insertFieldKey} onValueChange={setInsertFieldKey}>
                    <SelectTrigger data-testid="insert-field-select"><SelectValue placeholder="Choose a field…" /></SelectTrigger>
                    <SelectContent>
                      {insertableFields.map((f) => (
                        <SelectItem key={f.key} value={f.key} data-testid={`insert-field-${f.key}`}>
                          {f.label || f.key}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setInsertText(null)}>Cancel</Button>
                    <Button className="bg-[#ffb020] hover:bg-[#e69a10] text-[#14140f]" onClick={confirmInsert}
                      disabled={!insertFieldKey} data-testid="insert-field-confirm">
                      Insert
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          )}
        </motion.aside>
      )}
    </AnimatePresence>
  );
}
